import { z } from 'zod';
import { yearMonthSchema, currencyCodeSchema, decimalStringSchema, emptyToNull } from './common';

export const budgetMethodSchema = z.enum(['classic', 'zero_based', 'envelope']);
export type BudgetMethod = z.infer<typeof budgetMethodSchema>;

/**
 * 💳 Bütçe özetinin (gelir/gider/kalan) hesaplanacağı varsayılan para hesabı.
 * null → tüm işlemler (geriye dönük). update'te null gönderilirse temizlenir.
 * '' (formdaki boş seçenek) → null: API de zararsızca kabul eder.
 * Hesap bütçe SAHİBİNE ait olmalı (sunucu doğrular).
 */
const defaultMoneyAccountIdField = z.preprocess(
  emptyToNull,
  z.string().uuid().nullable().optional(),
);

/**
 * 💱 Bütçe totals/raporlarının kanonik para birimi (Faz G). null/verilmezse
 * sahibin user.baseCurrency'sine düşer. Değişirse dönem cachedTotals invalidate olur.
 * '' (formdaki "kullanıcı varsayılanı" seçeneği) → null.
 */
const budgetCurrencyField = z.preprocess(emptyToNull, currencyCodeSchema.nullable().optional());

export const createBudgetSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  isDefault: z.boolean().optional().default(false),
  /**
   * 🗓️ Bir sonraki dönemi otomatik açma günü: 0 = ayın son günü, 1-28 = o gün.
   * null → kapalı. update'te undefined = dokunma, null = kapat.
   */
  autoPeriodDay: z.number().int().min(0).max(28).nullable().optional(),
  sortOrder: z.number().int().optional(),
  method: budgetMethodSchema.optional().default('classic'),
  defaultMoneyAccountId: defaultMoneyAccountIdField,
  currency: budgetCurrencyField,
});
export type CreateBudgetInput = z.infer<typeof createBudgetSchema>;

/**
 * 📝 Bütçe formu (web RHF resolver'ı). createBudgetSchema ile aynı kurallar; hesap ve döviz
 * `<select>`'lerinin boş seçeneği ('') null'a çevrilir. Girdi tipi (`BudgetFormInput`) ''
 * kabul eder, çıktı tipi (`BudgetFormValues`) API gövdesiyle aynıdır.
 */
export const budgetFormSchema = createBudgetSchema;
export type BudgetFormInput = z.input<typeof budgetFormSchema>;
export type BudgetFormValues = z.output<typeof budgetFormSchema>;

export const updateBudgetSchema = createBudgetSchema.partial().extend({
  /**
   * ⚓ Çapalı bütçede özet hesabı ya da (çözülen) bütçe dövizi değişiyorsa zorunlu onay:
   * true → çapa aynı kayıtta kaldırılır (devir zinciri korunur). Gönderilmezse sunucu
   * 409 `BUDGET_ANCHORED` döner. Değişiklik olmadan true gönderilirse yalnız çapa kaldırılır.
   */
  clearAnchor: z.boolean().optional(),
});
export type UpdateBudgetInput = z.infer<typeof updateBudgetSchema>;

/**
 * ⚓ "Banka ile eşitle" — bütçeyi varsayılan para hesabına çapalar.
 * `bankBalance`: bankadaki BUGÜNKÜ GERÇEK bakiye. Uygulamadaki hesap bakiyesi (bugüne kadar
 * gerçekleşmiş; ileri tarihli transfer/düzeltmeler hariç) bu değere, çapa ayından önceye
 * tarihli bir düzeltme ile eşitlenir.
 *
 * `asOf` yalnız 'today'. 'period_start' (ay başı bakiyesi) KAPALI: ekstredeki tarih bazlı ay
 * başı bakiyesi dönem bazlı defterle kıyaslanınca dönem ayı ≠ banka ayı olan her kalem
 * (maaş döngüsü, ön-açılış kopyaları) düzeltmeye gömülüyordu (bkz. ledger-02).
 */
export const setBudgetAnchorSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  bankBalance: decimalStringSchema,
  asOf: z.literal('today').optional().default('today'),
});
export type SetBudgetAnchorInput = z.infer<typeof setBudgetAnchorSchema>;

export const periodParamsSchema = yearMonthSchema;
export type PeriodParams = z.infer<typeof periodParamsSchema>;

export const unlockPeriodSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type UnlockPeriodInput = z.infer<typeof unlockPeriodSchema>;

export const bulkPeriodsSchema = z.object({
  budgetIds: z.array(z.string().uuid()).min(1).max(50),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
});
export type BulkPeriodsInput = z.infer<typeof bulkPeriodsSchema>;

/**
 * 🧩 C10 — POST …/periods/:year/:month/recurring-gaps/apply gövdesi: dry-run listesinden
 * kullanıcının seçtiği kaynak işlemler (eski cron'un tekrarsız açtığı dönemi onarma).
 */
export const applyRecurringGapsSchema = z.object({
  sourceTransactionIds: z.array(z.string().uuid()).min(1).max(500),
});
export type ApplyRecurringGapsInput = z.infer<typeof applyRecurringGapsSchema>;
